import type { MetaAd, MetaAdset, MetaCampaign, MetaPerf, MetaStructureResponse } from '@nono/contract';
import { Play, RotateCcw, Stethoscope, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { DateRangePicker } from '@/components/shell/DateRangePicker';
import { cn } from '@/lib/utils';
import { AccountCanvas, campaignLabel, type Offsets, type Selection } from './AccountMap';
import { api } from '@/lib/api';
import { AdPreview } from './AdPreview';
import { ENTRY_BY_ID, entryForAd } from './blueprint';
import { MetaShell, Toggle } from './MetaShell';
import {
  costPer,
  creativeStats,
  holdTone,
  hookTone,
  pctStr,
  periodLabel,
  type Finding,
  flatAds,
  healthChecks,
  int,
  isLive,
  kr,
  type Period,
  type ResultKind,
  resultKind,
  resultsOf,
  short,
  type Verdict,
  unitCosts,
  verdict,
  useMetaStructure,
} from './model';

/*
 * Meta → Kontoen. The live ad account drawn as a map, the health checks that
 * hold it up against the blueprint, and the list of what is running right now.
 */

const VERDICT_LABEL: Record<Verdict, string> = {
  good: 'Billig',
  ok: 'Middels',
  bad: 'Dyr',
  none: 'Brukte penger, null resultat',
  young: 'For lite data',
};
const VERDICT_TONE: Record<Verdict, 'good' | 'neutral' | 'bad' | 'warn'> = {
  good: 'good',
  ok: 'neutral',
  bad: 'bad',
  none: 'bad',
  young: 'neutral',
};
const FINDING_TONE: Record<Finding['tone'], 'bad' | 'warn' | 'neutral'> = {
  bad: 'bad',
  warn: 'warn',
  info: 'neutral',
};

const OBJECTIVE: Record<string, string> = {
  OUTCOME_LEADS: 'Leads',
  OUTCOME_ENGAGEMENT: 'Engasjement',
  OUTCOME_SALES: 'Salg',
  OUTCOME_TRAFFIC: 'Trafikk',
  OUTCOME_AWARENESS: 'Synlighet',
};
const GOAL: Record<string, string> = {
  QUALITY_LEAD: 'Kvalitetslead (skjema)',
  LEAD_GENERATION: 'Lead (skjema)',
  OFFSITE_CONVERSIONS: 'Konvertering på nettsiden',
};

export function MetaAccountPage() {
  const q = useMetaStructure();
  const [period, setPeriod] = useState<Period>('lifetime');
  const [onlyLive, setOnlyLive] = useState(false);
  const [selected, setSelected] = useState<Selection>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [flyTo, setFlyTo] = useState<string[] | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [previewAd, setPreviewAd] = useState<{ id: string; name: string } | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  // Shared board layout: where things were dragged (same on every device).
  const qc = useQueryClient();
  const layoutQ = useQuery({
    queryKey: ['meta-layout'],
    queryFn: async () => {
      try {
        return (await api.metaLayout()).positions;
      } catch (err) {
        if (import.meta.env.DEV) return {} as Offsets; // no local backend
        throw err;
      }
    },
    staleTime: 60_000,
  });
  const offsets: Offsets = layoutQ.data ?? {};
  const saveMut = useMutation({
    mutationFn: async (m: { id: string; dx: number; dy: number }) => {
      try {
        return await api.metaLayoutSave([m]);
      } catch (err) {
        if (import.meta.env.DEV) return null; // no local backend: keep the drag on screen
        throw err;
      }
    },
    onMutate: (m) => {
      qc.setQueryData<Offsets>(['meta-layout'], (prev) => {
        const next = { ...(prev ?? {}) };
        if (m.dx === 0 && m.dy === 0) delete next[m.id];
        else next[m.id] = { dx: m.dx, dy: m.dy };
        return next;
      });
    },
    onError: () => void qc.invalidateQueries({ queryKey: ['meta-layout'] }),
  });
  const resetMut = useMutation({
    mutationFn: () => api.metaLayoutReset(),
    onMutate: () => qc.setQueryData<Offsets>(['meta-layout'], {}),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['meta-layout'] }),
  });
  const moved = Object.keys(offsets).length;

  const d = q.data?.data;
  const findings = useMemo(() => (d ? healthChecks(d) : []), [d]);
  const highlight = useMemo(
    () => new Set(findings.find((f) => f.id === focus)?.ids ?? []),
    [findings, focus],
  );

  const toggleFocus = (f: Finding) => {
    const next = focus === f.id ? null : f.id;
    setFocus(next);
    if (next && f.ids.length) setFlyTo([...f.ids]);
  };

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !d) return <ErrorState onRetry={() => void q.refetch()} />;

  const fx = d.fxAedToNok;
  const all = flatAds(d);
  const liveAds = all.filter((f) => isLive(f.ad) && isLive(f.adset) && isLive(f.campaign));
  const liveSets = d.campaigns.filter(isLive).flatMap((c) => c.adsets.filter(isLive));
  const liveBudget = d.campaigns
    .filter(isLive)
    .reduce(
      (n, c) => n + (c.dailyBudget ?? c.adsets.filter(isLive).reduce((m, s) => m + (s.dailyBudget ?? 0), 0)),
      0,
    );
  const t = d.totals[period];
  const periodWord = period === 'lifetime' ? 'totalt' : periodLabel(d);
  const unit = unitCosts(d, period);

  return (
    <MetaShell
      title="Meta-kontoen"
      lead={
        <>
          {d.campaigns.length} kampanjer, {d.campaigns.reduce((n, c) => n + c.adsets.length, 0)}{' '}
          annonsesett og {all.length} annonser har kjørt. Nå går{' '}
          <b className="text-fg">
            {d.campaigns.filter(isLive).length} kampanjer, {liveSets.length} annonsesett og{' '}
            {liveAds.length} annonser
          </b>{' '}
          for {kr(liveBudget * fx)} om dagen.
        </>
      }
    >
      {q.data?.snapshot && (
        <p className="rounded-md border border-warn/40 bg-warn-tint px-3 py-2 text-label text-warn-fg">
          Utviklingsvisning: øyeblikksbilde av kontoen fra {fmtTime(d.fetchedAt)}, ikke live.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard source="meta" label={`Brukt ${periodWord}`} value={kr(t.spend * fx)} sub={`${int(Math.round(t.spend))} ${d.account.currency}`} />
        <StatCard source="booking" label="Bookinger på nettsiden" value={int(t.schedules)} sub={unit.costPerBooking ? `${kr(unit.costPerBooking)} per booking i booking-kampanjene` : 'Ingen i perioden'} {...(t.schedules ? {} : { status: 'bad' as const })} />
        <StatCard source="lead" label="Skjemalead" value={int(t.leads)} sub={unit.costPerLead ? `${kr(unit.costPerLead)} per lead i skjema-kampanjene` : '–'} />
        <StatCard source="meta" label="Klikk" value={int(t.clicks)} sub={t.impressions ? `CTR ${((t.clicks / t.impressions) * 100).toFixed(1).replace('.', ',')} %` : '–'} />
      </div>

      <Card ref={mapRef} className="scroll-mt-4 overflow-hidden">
        <AccountCanvas
          data={d}
          period={period}
          onlyLive={onlyLive}
          selected={selected}
          highlight={highlight}
          onSelect={setSelected}
          offsets={offsets}
          onMove={(m) => saveMut.mutate(m)}
          flyTo={flyTo}
          toolbar={
            <>
              <Toggle
                value={period}
                onChange={setPeriod}
                options={[
                  { value: 'lifetime', label: 'Hele perioden' },
                  { value: 'recent', label: 'Valgt periode' },
                ]}
              />
              {period === 'recent' && (
                <div className="rounded-lg bg-surface/90 shadow-card backdrop-blur">
                  <DateRangePicker />
                </div>
              )}
              {moved > 0 && (
                <button
                  type="button"
                  title="Legg alt tilbake der systemet plasserte det (gjelder alle enheter)"
                  onClick={() => {
                    if (window.confirm(`Tilbakestill oppsettet? ${moved} flyttede elementer legges tilbake, på alle enheter.`))
                      resetMut.mutate();
                  }}
                  className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-surface/90 px-3 text-label text-fg-muted shadow-card backdrop-blur transition-fast hover:text-fg sm:h-8"
                >
                  <RotateCcw className="size-4" aria-hidden />
                  Tilbakestill oppsett
                  <span className="tabular font-semibold">{moved}</span>
                </button>
              )}
              {(q.isFetching || saveMut.isPending) && (
                <span className="text-micro text-fg-subtle">{saveMut.isPending ? 'Lagrer …' : 'Henter …'}</span>
              )}
              <Toggle
                value={onlyLive ? 'live' : 'all'}
                onChange={(v) => setOnlyLive(v === 'live')}
                options={[
                  { value: 'all', label: 'Alt' },
                  { value: 'live', label: 'Bare aktive' },
                ]}
              />
              {findings.length > 0 && (
                <button
                  type="button"
                  onClick={() => setDrawer((v) => !v)}
                  aria-pressed={drawer}
                  className={cn(
                    'flex h-10 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-label shadow-card transition-fast sm:h-8',
                    drawer ? 'border-warn/60 bg-warn-tint text-warn-fg' : 'border-border bg-surface/90 text-fg-muted backdrop-blur hover:text-fg',
                  )}
                >
                  <Stethoscope className="size-4" aria-hidden />
                  Helsesjekk
                  <span className="tabular font-semibold">{findings.length}</span>
                </button>
              )}
            </>
          }
          legend={<Legend />}
          overlay={
            <>
              {drawer && (
                <aside className="absolute bottom-14 left-2 top-14 z-20 flex w-[min(360px,calc(100%-16px))] flex-col overflow-hidden rounded-lg border border-border bg-surface/95 shadow-lg backdrop-blur sm:left-3">
                  <div className="flex items-center justify-between border-b border-border px-3 py-2">
                    <span className="text-section text-fg">Helsesjekk</span>
                    <button type="button" onClick={() => setDrawer(false)} aria-label="Lukk" className="flex size-8 items-center justify-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg">
                      <X className="size-4" aria-hidden />
                    </button>
                  </div>
                  <ul className="flex flex-1 flex-col gap-1.5 overflow-y-auto p-2">
                    {findings.map((f) => (
                      <li key={f.id}>
                        <FindingButton f={f} on={focus === f.id} onClick={() => toggleFocus(f)} compact />
                      </li>
                    ))}
                  </ul>
                </aside>
              )}
              {focus && (
                <div className="absolute bottom-14 left-1/2 z-20 flex max-w-[calc(100%-16px)] -translate-x-1/2 items-center gap-2 rounded-full border border-warn/40 bg-warn-tint px-3 py-1.5 text-label text-warn-fg shadow-card">
                  <span className="min-w-0 truncate">Lyser opp: {findings.find((f) => f.id === focus)?.title}</span>
                  <button type="button" onClick={() => setFocus(null)} className="shrink-0 font-medium underline-offset-2 hover:underline">
                    Fjern
                  </button>
                </div>
              )}
              {selected && (
                <Detail
                  d={d}
                  sel={selected}
                  period={period}
                  onClose={() => setSelected(null)}
                  onPreview={(id, name) => setPreviewAd({ id, name })}
                />
              )}
              {previewAd && (
                <AdPreview adId={previewAd.id} name={previewAd.name} onClose={() => setPreviewAd(null)} />
              )}
            </>
          }
        />
      </Card>

      {findings.length > 0 && (
        <Card className="p-3 sm:p-4">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-section text-fg">Helsesjekk mot systemet</h2>
            <span className="text-label text-fg-subtle">Trykk for å lyse det opp i kartet</span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {findings.map((f) => (
              <li key={f.id}>
                <FindingButton
                  f={f}
                  on={focus === f.id}
                  onClick={() => {
                    toggleFocus(f);
                    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <LiveList
        d={d}
        onPick={(id) => {
          setSelected({ kind: 'ad', id });
          setFlyTo([id]);
          mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />
    </MetaShell>
  );
}

function FindingButton({ f, on, onClick, compact }: { f: Finding; on: boolean; onClick: () => void; compact?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'w-full rounded-md border px-3 py-2 text-left transition-fast',
        on ? 'border-warn/60 bg-warn-tint' : 'border-border hover:bg-surface-2',
      )}
    >
      <div className={cn('flex items-start gap-2', compact && 'flex-col gap-1')}>
        <Badge tone={FINDING_TONE[f.tone]} className={compact ? undefined : 'mt-0.5'}>
          {f.tone === 'bad' ? 'Kritisk' : f.tone === 'warn' ? 'Viktig' : 'Verdt å vite'}
        </Badge>
        <div className="min-w-0">
          <div className="text-body font-medium text-fg">{f.title}</div>
          <div className="text-label text-fg-muted">{f.body}</div>
        </div>
      </div>
    </button>
  );
}

function Legend() {
  const [open, setOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 900);
  const dot = (fill: string, stroke?: string) => (
    <svg viewBox="0 0 12 12" className="size-3 shrink-0" aria-hidden>
      <circle cx="6" cy="6" r="4.5" fill={fill} stroke={stroke ?? 'none'} strokeWidth="1.3" />
    </svg>
  );
  if (!open)
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 items-center gap-1.5 rounded-full border border-border bg-surface/90 px-3 text-label text-fg-muted shadow-card backdrop-blur hover:text-fg sm:h-8"
      >
        {dot('var(--good)')}
        {dot('var(--bad)')} Forklaring
      </button>
    );
  return (
    <div className="flex max-w-[640px] flex-wrap items-center gap-x-3.5 gap-y-1.5 rounded-lg border border-border bg-surface/90 px-3 py-2 text-label text-fg-muted shadow-card backdrop-blur">
      <span className="flex items-center gap-1.5">{dot('var(--brand)')} Kampanje</span>
      <span className="flex items-center gap-1.5">{dot('var(--surface)', 'var(--accent-booking)')} Annonsesett: booking</span>
      <span className="flex items-center gap-1.5">{dot('var(--surface)', 'var(--accent-lead)')} Annonsesett: skjema</span>
      <span className="flex items-center gap-1.5">{dot('var(--good)')} Billig</span>
      <span className="flex items-center gap-1.5">{dot('var(--fg-muted)')} Middels</span>
      <span className="flex items-center gap-1.5">{dot('var(--bad)')} Dyr</span>
      <span className="flex items-center gap-1.5">{dot('transparent', 'var(--bad)')} Null resultat</span>
      <span className="flex items-center gap-1.5">{dot('transparent', 'var(--fg-subtle)')} For lite data</span>
      <span className="text-fg-subtle">Størrelse = penger · blek = går ikke · dra en prikk for å flytte den · dra bakgrunnen for å panorere · ⌘/Ctrl + rull for å zoome</span>
      <button type="button" onClick={() => setOpen(false)} className="ml-auto text-fg-subtle underline-offset-2 hover:text-fg hover:underline">
        Skjul
      </button>
    </div>
  );
}

function find(d: MetaStructureResponse, sel: NonNullable<Selection>) {
  for (const c of d.campaigns) {
    if (sel.kind === 'campaign' && c.id === sel.id) return { c };
    for (const s of c.adsets) {
      if (sel.kind === 'adset' && s.id === sel.id) return { c, s };
      for (const a of s.ads) if (sel.kind === 'ad' && a.id === sel.id) return { c, s, a };
    }
  }
  return null;
}

function Detail({
  d,
  sel,
  period,
  onClose,
  onPreview,
}: {
  d: MetaStructureResponse;
  sel: NonNullable<Selection>;
  period: Period;
  onClose: () => void;
  onPreview: (adId: string, name: string) => void;
}) {
  const hit = find(d, sel);
  if (!hit) return null;
  const { c, s, a } = hit as { c: MetaCampaign; s?: MetaAdset; a?: MetaAd };
  const fx = d.fxAedToNok;
  const kind: ResultKind = s ? resultKind(s) : c.adsets.some((x) => resultKind(x) === 'booking') ? 'booking' : 'lead';
  const entity = a ?? s ?? c;
  const door = a ? entryForAd(a.name) : null;
  const rows: [string, string][] = [];
  if (!s) {
    const [l1, l2] = campaignLabel(c.name);
    rows.push(['Kampanje', `${l1} · ${l2}`]);
    rows.push(['Mål', OBJECTIVE[c.objective ?? ''] ?? c.objective ?? '–']);
    rows.push(['Budsjett', c.dailyBudget ? `${kr(c.dailyBudget * fx)}/dag på kampanjen (CBO)` : 'På annonsesettene']);
    rows.push(['Annonsesett', String(c.adsets.length)]);
  } else if (!a) {
    rows.push(['Optimaliserer for', `${GOAL[s.optimizationGoal ?? ''] ?? s.optimizationGoal ?? '–'}${s.conversionEvent ? ` · ${s.conversionEvent}` : ''}`]);
    rows.push(['Alder', s.ageMin ? `${s.ageMin}–${s.ageMax ?? '65+'}${s.ageMax === 65 ? '+' : ''}` : '–']);
    rows.push(['Område', s.geo ?? '–']);
    rows.push(['Advantage+ målgruppe', s.advantageAudience == null ? '–' : s.advantageAudience ? 'På' : 'Av']);
    rows.push(['Annonser', `${s.ads.filter(isLive).length} aktive av ${s.ads.length}`]);
  } else {
    rows.push(['Annonsesett', short(s.name)]);
    rows.push(['Format', a.format === 'video' ? 'Video' : a.format === 'image' ? 'Bilde' : 'Ukjent (lest fra navnet)']);
    rows.push(['Inngangsdør', door ? (ENTRY_BY_ID.get(door)?.name ?? door) : 'Ikke klassifisert']);
    if (a.createdTime) rows.push(['Laget', fmtDay(a.createdTime.slice(0, 10))]);
  }
  rows.push(['Status', statusWord(entity.status)]);

  return (
    <section
      aria-label="Detaljer"
      className="absolute right-2 top-14 z-20 max-h-[calc(100%-72px)] w-[min(340px,calc(100%-16px))] overflow-y-auto rounded-lg border border-border bg-surface/95 p-3 shadow-lg backdrop-blur sm:right-3 sm:p-4"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-micro font-semibold uppercase tracking-wider text-fg-subtle">
            {a ? 'Annonse' : s ? 'Annonsesett' : 'Kampanje'}
          </div>
          <div className="break-words text-section text-fg">{a ? a.name : s ? short(s.name) : campaignLabel(c.name)[0]}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Lukk" className="flex size-8 shrink-0 items-center justify-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg">
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-label">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-fg-subtle">{k}</dt>
            <dd className="min-w-0 break-words text-fg">{v}</dd>
          </div>
        ))}
      </dl>
      {a && (
        <button
          type="button"
          onClick={() => onPreview(a.id, a.name)}
          className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-md bg-brand text-label font-medium text-brand-fg transition-fast hover:bg-brand-hover sm:h-9"
        >
          <Play className="size-4" aria-hidden /> Se annonsen
        </button>
      )}
      {a && <CreativeBlock p={period === 'recent' ? a.recent : a.lifetime} fx={fx} title={period === 'recent' ? `Kreativ · ${periodLabel(d)}` : 'Kreativ · hele perioden'} />}
      <PerfBlock title="Hele perioden" p={entity.lifetime} kind={kind} fx={fx} muted={period === 'recent'} />
      <PerfBlock title={`Valgt periode · ${periodLabel(d)}`} p={entity.recent} kind={kind} fx={fx} muted={period !== 'recent'} />
    </section>
  );
}

function PerfBlock({ title, p, kind, fx, muted }: { title: string; p: MetaPerf; kind: ResultKind; fx: number; muted?: boolean }) {
  const v = verdict(p, kind, fx);
  const n = resultsOf(p, kind);
  return (
    <div className={cn('mt-3 rounded-md bg-surface-2 p-2.5', muted && 'opacity-80')}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-micro font-semibold uppercase tracking-wider text-fg-subtle">{title}</span>
        <Badge tone={VERDICT_TONE[v]}>{VERDICT_LABEL[v]}</Badge>
      </div>
      <div className="grid grid-cols-3 gap-2 text-label">
        <Num k="Brukt" v={kr(p.spend * fx)} />
        <Num k={kind === 'booking' ? 'Bookinger' : 'Lead'} v={int(n)} />
        <Num k={kind === 'booking' ? 'Per booking' : 'Per lead'} v={kr(costPer(p, kind, fx))} />
        <Num k="Klikk" v={int(p.clicks)} />
        <Num k="CTR" v={p.impressions ? `${((p.clicks / p.impressions) * 100).toFixed(1).replace('.', ',')} %` : '–'} />
        <Num k="Frekvens" v={p.frequency ? `≈${p.frequency.toFixed(1).replace('.', ',')}` : '–'} />
      </div>
    </div>
  );
}

function CreativeBlock({ p, fx, title }: { p: MetaPerf; fx: number; title: string }) {
  const c = creativeStats(p, fx);
  const tone = (t: 'good' | 'bad' | null) => (t === 'good' ? 'text-good-fg' : t === 'bad' ? 'text-bad-fg' : 'text-fg');
  const max = Math.max(0.0001, ...c.curve.map((x) => x.v ?? 0));
  return (
    <div className="mt-3 rounded-md bg-surface-2 p-2.5">
      <div className="mb-1.5 text-micro font-semibold uppercase tracking-wider text-fg-subtle">{title}</div>
      {c.isVideo ? (
        <>
          <div className="grid grid-cols-3 gap-2 text-label">
            <div className="min-w-0" title="3-sekunders avspillinger ÷ visninger. 30 %+ er sterkt, under 20 % er svakt.">
              <div className="text-micro text-fg-subtle">Hook rate</div>
              <div className={cn('tabular font-semibold', tone(hookTone(c.hook)))}>{pctStr(c.hook)}</div>
            </div>
            <div className="min-w-0" title="ThruPlays (15 s eller hele) ÷ 3-sekunders avspillinger. 40 %+ er sterkt, under 20 % er svakt.">
              <div className="text-micro text-fg-subtle">Hold rate</div>
              <div className={cn('tabular font-semibold', tone(holdTone(c.hold)))}>{pctStr(c.hold)}</div>
            </div>
            <Num k="Snitt sett" v={c.avgWatch === null ? '–' : `${c.avgWatch.toFixed(1).replace('.', ',')} s`} />
          </div>
          <div className="mt-2.5">
            <div className="mb-1 text-micro text-fg-subtle">Hvor langt folk så (av alle som fikk den vist)</div>
            <div className="flex h-16 items-end gap-1.5">
              {c.curve.map((x) => (
                <div key={x.label} className="flex flex-1 flex-col items-center gap-0.5">
                  <span className="tabular text-micro text-fg-muted">{pctStr(x.v, 0)}</span>
                  <div
                    className="w-full rounded-sm bg-[var(--accent-click)]"
                    style={{ height: `${Math.max(2, ((x.v ?? 0) / max) * 36)}px`, opacity: 0.85 }}
                  />
                  <span className="text-micro text-fg-subtle">{x.label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <p className="text-label text-fg-muted">Bildeannonse, eller ingen videoavspillinger i perioden.</p>
      )}
      <div className="mt-2.5 grid grid-cols-3 gap-2 text-label">
        <Num k="CTR (lenke)" v={pctStr(c.ctr, 2)} />
        <Num k="CTR (ut)" v={pctStr(c.outboundCtr, 2)} />
        <Num k="Engasjement" v={pctStr(c.engagementRate)} />
        <Num k="CPM" v={kr(c.cpm)} />
        <Num k="CPC" v={kr(c.cpc)} />
        <Num k="Visninger" v={int(p.impressions)} />
      </div>
    </div>
  );
}

function Num({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <div className="text-micro text-fg-subtle">{k}</div>
      <div className="tabular truncate font-medium text-fg">{v}</div>
    </div>
  );
}

function LiveList({ d, onPick }: { d: MetaStructureResponse; onPick: (id: string) => void }) {
  const fx = d.fxAedToNok;
  const rows = flatAds(d)
    .filter((f) => isLive(f.ad) && isLive(f.adset) && isLive(f.campaign))
    .sort((x, y) => y.ad.recent.spend - x.ad.recent.spend);
  if (!rows.length) return null;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-baseline justify-between gap-2 border-b border-border px-3 py-2.5 sm:px-4">
        <h2 className="text-section text-fg">Det som går nå</h2>
        <span className="text-label text-fg-subtle">Valgt periode {periodLabel(d)} · sortert etter penger</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-label">
          <thead className="text-left text-fg-subtle">
            <tr>
              <th className="px-3 py-2 font-medium sm:px-4">Annonse</th>
              <th className="px-3 py-2 font-medium">Inngangsdør</th>
              <th className="px-3 py-2 text-right font-medium">Brukt</th>
              <th className="px-3 py-2 text-right font-medium">Resultat</th>
              <th className="px-3 py-2 text-right font-medium">Per resultat</th>
              <th className="px-3 py-2 font-medium">Vurdering</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ ad, adset }) => {
              const kind = resultKind(adset);
              const v = verdict(ad.recent, kind, fx);
              const door = entryForAd(ad.name);
              return (
                <tr key={ad.id} className="cursor-pointer border-t border-border hover:bg-surface-2" onClick={() => onPick(ad.id)}>
                  <td className="max-w-[280px] px-3 py-2 sm:px-4">
                    <div className="truncate text-fg">{ad.name}</div>
                    <div className="truncate text-micro text-fg-subtle">{short(adset.name)}</div>
                  </td>
                  <td className="px-3 py-2 text-fg-muted">{door ? ENTRY_BY_ID.get(door)?.name : '–'}</td>
                  <td className="tabular px-3 py-2 text-right text-fg">{kr(ad.recent.spend * fx)}</td>
                  <td className="tabular px-3 py-2 text-right text-fg">
                    {int(resultsOf(ad.recent, kind))} {kind === 'booking' ? 'book.' : 'lead'}
                  </td>
                  <td className="tabular px-3 py-2 text-right text-fg">{kr(costPer(ad.recent, kind, fx))}</td>
                  <td className="px-3 py-2">
                    <Badge tone={VERDICT_TONE[v]}>{VERDICT_LABEL[v]}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function statusWord(s: string): string {
  return (
    { active: 'Aktiv', paused: 'Stoppet', parent_paused: 'Stoppet (overordnet)', issue: 'Har et problem', archived: 'Arkivert' }[s] ?? s
  );
}
export function fmtDay(iso: string): string {
  const [y, m, dd] = iso.split('-');
  return `${dd}.${m}.${y}`;
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' });
}
